import { useGreffers } from '@/api/greffers';
import { useInstances } from '@/api/greffons';
import type { Greffer, GreffonInstance } from '@/api/types';
import { greffonChipKey, isGrefferConnected } from '@/lib/status';

export type OnboardingStep = 'connect' | 'deploy' | 'start';

/**
 * An instance counts as "running" only when the liveness-aware `greffonChipKey`
 * resolves to `'started'`. That excludes both a `STARTED` on a down greffer
 * (`greffer-not-running`) and a `STARTED` on an UNREACHABLE greffer
 * (`state-unknown`): in the latter the manager can't confirm the instance is
 * actually up, so onboarding must not declare the goal met or advertise a live
 * URL for it (mirrors the gating in MyGreffons).
 */
export const isRunning = (i: GreffonInstance) => greffonChipKey(i) === 'started';

export interface OnboardingState {
  done: Record<OnboardingStep, boolean>;
  /**
   * True until onboarding is complete — i.e. until a running instance exists.
   * The hook ANDs this with `ready` before using it for the landing redirect.
   */
  incomplete: boolean;
}

/**
 * Pure derivation of onboarding state from raw query data — unit-testable
 * without React or TanStack Query. The greffer check uses the numeric `GREFFER`
 * constant; the running check uses the liveness-aware `isRunning`.
 */
export function deriveOnboarding(greffers: Greffer[], instances: GreffonInstance[]): OnboardingState {
  // REGISTERED is not the same as reachable: a status-only check unmounted the
  // connect flow and unlocked deployment onto a host that answers 502, which is
  // the exact lie the tunnel-readiness epic exists to remove, one layer up.
  //
  // Shared with ConnectGrefferFlow rather than restated here. This condition
  // written out twice is what produced the Get-started dead end: this side
  // said "not connected, keep the flow mounted", the flow's own copy said
  // "registered, nothing to track", and between them the only offered action
  // created a second greffer.
  const connect = greffers.some(isGrefferConnected);
  const deploy = instances.length > 0;
  const start = instances.some(isRunning);
  return { done: { connect, deploy, start }, incomplete: !start };
}

/**
 * Onboarding state derived from the existing `useGreffers` / `useInstances`
 * queries — no new persistence. `firstRun` is true until the goal ("one app
 * running") is met, so the landing guard keeps a user on Get-started through
 * connect → deploy → start (see hld-get-started-flow.md §1).
 */
export function useOnboarding() {
  const greffers = useGreffers();
  const instances = useInstances();

  const ready = greffers.isSuccess && instances.isSuccess;
  const isError = greffers.isError || instances.isError;
  const { done, incomplete } = deriveOnboarding(greffers.data ?? [], instances.data ?? []);

  const firstRun = ready && incomplete;
  const retry = () => {
    greffers.refetch();
    instances.refetch();
  };

  return { ready, isError, retry, firstRun, done };
}
